import { ChangeDetectionStrategy, Component, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { EnvironmentService } from '@galaxy/core';
import { AppPartnerService, AppSettings } from '@galaxy/marketplace-apps';
import { FeatureFlagService } from '@galaxy/partner';
import { AccountsService, ActivationStatus } from '@vendasta/accounts';
import { GetMultiActivationStatusesResponseAppsAndAddonsActivationStatuses } from '@vendasta/accounts/lib/_internal/objects/api';
import { ConversationChannel, GlobalParticipantType, SubjectParticipant } from '@vendasta/conversation';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { newAccountServiceContext, SSOService } from '@vendasta/sso';
import { BehaviorSubject, combineLatest, EMPTY, firstValueFrom, Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { ConversationService } from '../../../../../../core/src/lib/state/conversation.service';
import {
  CustomerVoiceAppIDs,
  DEFAULT_CV_ICON,
  DEFAULT_CV_NAME,
  SMB_MESSAGE_PARTNER_FEATURE,
} from '../../../../../../core/src/lib/inbox.constants';
import { InboxService } from '../../../../../../core/src/lib/inbox.service';
import { ListingService, ReviewLink } from '../../../../../../core/src/lib/listing.service';
import { ParticipantService } from '../../../../../../core/src/lib/participant.service';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_HOST_APP_INTERFACE_TOKEN,
  PARTNER_ID_TOKEN,
} from '../../../../../../core/src/lib/tokens';

import { MatMenu } from '@angular/material/menu';

interface CVBrandingInfo {
  name: string;
  iconUrl: string;
}

@Component({
  selector: 'inbox-insert-review-link',
  templateUrl: './inbox-insert-review-link.component.html',
  styleUrls: ['./inbox-insert-review-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InboxInsertReviewLinkComponent {
  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);
  private readonly participantService = inject(ParticipantService);

  @Output() insertLink = new EventEmitter();
  @ViewChild('menuCustomerVoice') customerVoiceMenu: MatMenu;
  @ViewChild('menuContactUs') contactUsMenu: MatMenu;

  private readonly loading$$ = new BehaviorSubject<boolean>(true);
  protected readonly loading$ = this.loading$$.asObservable();

  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);

  customerVoiceAppIsActive$: Observable<boolean> = this.accountGroupId$.pipe(
    switchMap((accountGroupId) => {
      return this.accountsService.getMultiActivationStatuses([accountGroupId]).pipe(
        catchError((err) => {
          console.warn('error to get activation status', err);
          return EMPTY;
        }),
        defaultIfEmpty([]),
        map((res: GetMultiActivationStatusesResponseAppsAndAddonsActivationStatuses[]) => {
          const activeApps = res.filter(
            (filter) =>
              filter.productId === CustomerVoiceAppIDs[this.environmentService.getEnvironment()] &&
              filter.status === ActivationStatus.ACTIVATION_STATUS_ACTIVATED,
          );
          return activeApps.length > 0;
        }),
      );
    }),
  );

  cvPreferredSitesSettingsLink$: Observable<string> = combineLatest([
    this.customerVoiceAppIsActive$,
    this.accountGroupId$,
  ]).pipe(
    switchMap(([customerVoiceAppIsActive, accountGroupId]) => {
      if (!customerVoiceAppIsActive) {
        return of(null);
      }
      return this.ssoService
        .getEntryUrl(
          CustomerVoiceAppIDs[this.environmentService.getEnvironment()],
          newAccountServiceContext(accountGroupId),
        )
        .pipe(
          catchError(() => of(null)),
          map((entryUrl) => {
            if (!entryUrl) {
              return null;
            }
            const url = new URL(
              entryUrl.replace('<accountId>', accountGroupId).replace('%3CaccountId%3E', accountGroupId),
            );
            url.searchParams.set('nextUrl', `/cv/account/${accountGroupId}/settings/preferred-sites`);
            return url.toString();
          }),
          shareReplay(1),
        );
    }),
  );

  customerVoiceAppInfo$ = combineLatest([this.partnerId$, this.conversationService.marketId$]).pipe(
    switchMap(([partnerId, marketId]) => {
      return this.appPartnerService
        .getAppSettings(CustomerVoiceAppIDs[this.environmentService.getEnvironment()], partnerId, marketId)
        .pipe(
          catchError(() => {
            console.error('error getting customer voice app settings');
            return EMPTY;
          }),
          defaultIfEmpty({ branding: { enabled: false } }),
          map((appSettings: AppSettings) => {
            return {
              name:
                appSettings?.branding?.name && appSettings?.branding?.enabled
                  ? appSettings?.branding?.name
                  : DEFAULT_CV_NAME,
              iconUrl:
                appSettings?.branding?.iconUrl && appSettings?.branding?.enabled
                  ? appSettings.branding.iconUrl
                  : DEFAULT_CV_ICON,
            };
          }),
        );
    }),
    map((branding) => {
      return {
        name: branding.name,
        iconUrl: branding.iconUrl,
      } as CVBrandingInfo;
    }),
  );

  enableMessagePartnerFeature$: Observable<boolean> = this.partnerId$.pipe(
    switchMap((partnerId) => {
      return this.featureFlagService.batchGetStatus(partnerId, '', [SMB_MESSAGE_PARTNER_FEATURE]);
    }),
    filter((ff) => !!ff),
    map((feature) => feature[SMB_MESSAGE_PARTNER_FEATURE]),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  reviewLinks$: Observable<ReviewLink[]> = this.listingService.reviewLinks$.pipe(
    catchError(() => {
      this.loading$$.next(false);
      return EMPTY;
    }),
    defaultIfEmpty([]),
    map((results) => {
      this.loading$$.next(false);
      return results;
    }),
  );

  constructor(
    private conversationService: ConversationService,
    private appPartnerService: AppPartnerService,
    private listingService: ListingService,
    private ssoService: SSOService,
    private accountsService: AccountsService,
    private featureFlagService: FeatureFlagService,
    private analyticsService: ProductAnalyticsService,
    private environmentService: EnvironmentService,
    private inboxService: InboxService,
  ) {}

  async useLink(reviewLink: ReviewLink): Promise<void> {
    const [partnerId, accountGroupId] = await Promise.all([
      firstValueFrom(this.partnerId$),
      firstValueFrom(this.accountGroupId$),
    ]);
    this.analyticsService.trackEvent('inbox-insert-review-link', reviewLink.sourceName, 'clicked', 0, {
      accountGroupId: accountGroupId,
      partnerId: partnerId,
      location: this.inboxService.platformLocation,
    });
    this.insertLink.emit(reviewLink.reviewUrl);
  }

  openNewCVPreferredSitesSettingsTab(cvPreferredSitesSettingsLink: string): void {
    window.open(cvPreferredSitesSettingsLink, '_blank');
  }

  sendMessage(): void {
    combineLatest([this.partnerId$, this.accountGroupId$, this.participantService.buildIAMUserParticipant()])
      .pipe(take(1))
      .subscribe(([partnerId, accountGroupId, currentIAMParticipant]) => {
        this.hostAppInterface.redirectToInternalConversation(
          currentIAMParticipant,
          [
            new SubjectParticipant({
              internalParticipantId: accountGroupId,
              participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
            }),
            new SubjectParticipant({
              internalParticipantId: partnerId,
              participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
            }),
          ],
          ConversationChannel.CONVERSATION_CHANNEL_INTERNAL,
        );
      });
  }
}
